<script lang="ts" setup>
import { computed } from "vue";
import { useForwardProps } from "../../../../utils/props.js";
import OnyxMenuItem from "../OnyxMenuItem/OnyxMenuItem.vue";
import type { OnyxMenuItemProps } from "../OnyxMenuItem/types.js";

const props = defineProps<
  OnyxMenuItemProps & {
    /**
     * The render context of the nav item.
     * Styling is changed based on the context prop.
     */
    context: "mobile" | "list" | "navbar" | "vertical-navbar";
  }
>();

const slots = defineSlots<{
  /**
   * Button/link text and additional inline content.
   */
  default(): unknown;
  /**
   * Children items of the nav item.
   */
  children(): unknown;
}>();

const menuItemProps = useForwardProps(props, OnyxMenuItem);
const hasChildren = computed(() => !!slots.children);
</script>

<template>
  <OnyxMenuItem
    v-bind="menuItemProps"
    :link="hasChildren && props.context !== 'navbar' ? undefined : props.link"
    :class="{
      'onyx-nav-item': true,
      'onyx-nav-item--active': props.active,
      [`onyx-nav-item--${props.context}`]: true,
      'onyx-nav-item--open': props.open,
    }"
    :open="props.context !== 'list' ? false : undefined"
  >
    <slot></slot>
    <template v-if="hasChildren" #children><slot name="children"></slot></template>
  </OnyxMenuItem>
</template>

<style lang="scss">
@use "../../../../styles/mixins/layers";

@include layers.override() {
  .onyx-nav-item--navbar,
  .onyx-nav-item--vertical-navbar {
    position: relative;
    height: 2.5rem;
    width: max-content;
    border-radius: var(--onyx-radius-sm);
    white-space: nowrap;
    --onyx-list-item-background-hover: transparent;
    --onyx-list-item-background-selected: var(--onyx-color-base-background-blank);
    --onyx-list-item-padding: var(--onyx-spacing-2xs) var(--onyx-spacing-md);

    &:has(:focus-visible) {
      outline: var(--onyx-outline-width) solid var(--onyx-color-component-focus-primary);
      color: var(--onyx-list-item-color);
    }

    &:hover {
      --onyx-list-item-background-selected: var(--onyx-color-base-neutral-200);
      --onyx-list-item-background-hover: var(--onyx-color-base-neutral-200);
    }

    & .onyx-menu-item__chevron {
      display: none;
    }

    &:has(.onyx-nav-item--active),
    &.onyx-nav-item--active {
      font-weight: var(--onyx-font-weight-semibold);
      color: var(--onyx-color-component-nav-label);
      --onyx-list-item-color-selected: var(--onyx-color-component-nav-label);

      &::after {
        content: " ";
        position: absolute;
        width: 100%;
        height: 0.125rem;
        bottom: calc(-1 * var(--onyx-spacing-2xs));
        border-radius: var(--onyx-radius-full) var(--onyx-radius-full) 0 0;
        background: var(--onyx-color-component-nav-stroke);
        z-index: 1;
      }
    }
  }

  .onyx-nav-item--mobile {
    --onyx-list-item-padding: var(--onyx-spacing-sm);
    color: var(--onyx-color-text-icons-neutral-intense);
    width: 100%;
    align-self: stretch;
    line-height: var(--onyx-font-line-height-md);
    text-align: start;
    font-size: var(--onyx-font-size-md);
    font-weight: var(--onyx-font-weight-regular);
    border-radius: var(--onyx-radius-sm);
    border: var(--onyx-1px-in-rem) solid var(--onyx-color-component-border-neutral);

    &:focus-within {
      background-color: var(--onyx-color-base-background-blank);
    }

    &:hover {
      background-color: var(--onyx-color-base-background-tinted);
    }

    &:has(.onyx-nav-item--active),
    &.onyx-nav-item--active {
      --onyx-list-item-background-selected: var(--onyx-color-base-primary-100);
      --onyx-list-item-color-selected: var(--onyx-color-component-nav-label);
      background-color: var(--onyx-list-item-background-selected);
      border-color: var(--onyx-color-base-primary-200);
      color: var(--onyx-color-text-icons-primary-bold);
      font-weight: 600;
    }

    &:has(.onyx-nav-item--active):hover,
    &.onyx-nav-item--active:hover {
      border-color: var(--onyx-component-border-primary);
    }

    &.onyx-nav-item--open {
      all: unset;
    }
  }
  .onyx-nav-item--vertical-navbar {
    width: 100%;

    &:has(.onyx-nav-item--active),
    &.onyx-nav-item--active {
      &::after {
        width: 0.125rem;
        height: 100%;
        left: calc(-1 * var(--onyx-spacing-2xs));
        bottom: auto;
        border-radius: 0 var(--onyx-radius-full) var(--onyx-radius-full) 0;
      }
    }
  }
}
</style>
